import { t, Trans } from "@lingui/macro";
import { Pill } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { Link } from "react-router";

import { Logo } from "@/client/components/logo";
import type { SystemStatus } from "@/client/hooks/use-status";
import { useStatus } from "@/client/hooks/use-status";

// Re-export for downstream consumers (024 app shell footer uses StatusPill from this module)
export { StatusPill } from "@/client/components/primitives/status-pill";

const PRODUCT_LINKS = [
  { id: "resume-builder", href: "/" },
  { id: "templates", href: "/templates" },
  { id: "ats-checker", href: "/tools/ats-checker" },
  { id: "profile-review", href: "/tools/linkedin-profile-review" },
  { id: "roaster", href: "/tools/resume-roast" },
] as const;

const COMPANY_LINKS = [
  { id: "about", href: "/about" },
  { id: "changelog", href: "/changelog" },
  { id: "security", href: "/security" },
  { id: "contact", href: "/contact" },
] as const;

const RESOURCE_LINKS = [
  { id: "help-center", href: "/help" },
  { id: "pricing", href: "/pricing" },
  { id: "refund-policy", href: "/refund-policy" },
  { id: "terms", href: "/terms" },
  { id: "privacy", href: "/privacy" },
] as const;

type StatusConfig = {
  variant: "success" | "warning" | "error" | "neutral";
  dot: boolean;
};

const STATUS_CONFIG: Record<SystemStatus, StatusConfig> = {
  operational: { variant: "success", dot: true },
  degraded: { variant: "warning", dot: true },
  down: { variant: "error", dot: true },
  unknown: { variant: "neutral", dot: true },
};

const ProductLinkLabel = ({ id }: { id: string }) => {
  if (id === "resume-builder") return <Trans>Resume builder</Trans>;
  if (id === "templates") return <Trans>Templates</Trans>;
  if (id === "ats-checker") return <Trans>ATS checker</Trans>;
  if (id === "profile-review") return <Trans>Profile review</Trans>;
  return <Trans>Roaster</Trans>;
};

const CompanyLinkLabel = ({ id }: { id: string }) => {
  if (id === "about") return <Trans>About</Trans>;
  if (id === "changelog") return <Trans>Changelog</Trans>;
  if (id === "security") return <Trans>Security</Trans>;
  return <Trans>Contact</Trans>;
};

const ResourceLinkLabel = ({ id }: { id: string }) => {
  if (id === "help-center") return <Trans>Help center</Trans>;
  if (id === "pricing") return <Trans>Pricing</Trans>;
  if (id === "refund-policy") return <Trans>Refund policy</Trans>;
  if (id === "terms") return <Trans>Terms</Trans>;
  return <Trans>Privacy</Trans>;
};

type FooterProps = {
  className?: string;
};

export const Footer = ({ className }: FooterProps) => {
  const { status } = useStatus();
  const statusConfig = STATUS_CONFIG[status];
  const year = new Date().getFullYear();

  return (
    <footer className={cn("border-t border-border", className)}>
      <div className="mx-auto grid max-w-[1280px] grid-cols-2 gap-10 px-6 py-14 md:grid-cols-5">
        {/* Brand column */}
        <div className="col-span-2">
          <Link aria-label={t`Apply Edge`} to="/">
            <Logo withText size={28} />
          </Link>
          <p className="mt-4 max-w-[36ch] text-[12.5px] text-muted-foreground">
            <Trans>
              AI-powered resume builder and application assistant. Build, score, and tailor resumes
              for any role — then send with one click.
            </Trans>
          </p>
          <div className="mt-6">
            <Pill dot={statusConfig.dot} variant={statusConfig.variant}>
              {status === "operational" && <Trans>All systems operational</Trans>}
              {status === "degraded" && <Trans>Degraded</Trans>}
              {status === "down" && <Trans>Down</Trans>}
              {status === "unknown" && <Trans>Status unknown</Trans>}
            </Pill>
          </div>
        </div>

        {/* Product column */}
        <div>
          <div className="text-xs font-medium text-muted-foreground">
            <Trans>Product</Trans>
          </div>
          <ul className="mt-4 space-y-2 text-[13px]">
            {PRODUCT_LINKS.map((link) => (
              <li key={link.href}>
                <Link className="text-foreground/80 hover:text-foreground" to={link.href}>
                  <ProductLinkLabel id={link.id} />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Company column */}
        <div>
          <div className="text-xs font-medium text-muted-foreground">
            <Trans>Company</Trans>
          </div>
          <ul className="mt-4 space-y-2 text-[13px]">
            {COMPANY_LINKS.map((link) => (
              <li key={link.href}>
                <Link className="text-foreground/80 hover:text-foreground" to={link.href}>
                  <CompanyLinkLabel id={link.id} />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Resources column */}
        <div>
          <div className="text-xs font-medium text-muted-foreground">
            <Trans>Resources</Trans>
          </div>
          <ul className="mt-4 space-y-2 text-[13px]">
            {RESOURCE_LINKS.map((link) => (
              <li key={link.href}>
                <Link className="text-foreground/80 hover:text-foreground" to={link.href}>
                  <ResourceLinkLabel id={link.id} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-border">
        <div className="mx-auto flex max-w-[1280px] flex-col items-start justify-between gap-3 px-6 py-5 text-[11.5px] text-muted-foreground md:flex-row md:items-center">
          <div>
            {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
            <Trans>© {year} Apply Edge · Dubai, AE</Trans>
          </div>
          {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
          <div className="font-mono">v2026.04 · public surface</div>
        </div>
      </div>
    </footer>
  );
};
